<!-- src/components/device/commands/WiredInputCard.vue -->
<!--
  Entrée filaire — partagé admin/client, sur la présentation du client, jugée plus
  compréhensible : deux questions posées en clair (« Quand l'entrée est-elle surveillée ? »,
  « La sirène doit-elle sonner ? ») plutôt que des interrupteurs nommés d'après le firmware.

  Deux réglages indépendants plutôt qu'un menu de profils tout faits : les quatre
  combinaisons sont légitimes (une alarme technique peut être sonore ou silencieuse), et c'est
  l'installation — pas nous — qui dit ce qui est branché au bout du fil.

  L'état de repos NO/NC (qui remplace le switch physique) et la remontée des changements d'état
  sont aussi au titulaire depuis le 06/10/2026 (le serveur les lui permettait déjà, l'écran les
  réservait à l'admin) : affichés selon la liste de droits du serveur (`peut`), comme le reste.
  `technique` (admin) les force, l'admin n'ayant pas de liste.
-->
<template>
  <div class="bg-white rounded-lg shadow p-4">
    <h3 class="font-semibold text-gray-800">{{ $t('entreeFilaire.titre') }}</h3>
    <!-- Avec un nom (05/10/2026) : « Porte du garage », branché sur la carte : … -->
    <i18n-t :keypath="device.wired_label ? 'entreeFilaire.resumeNomme' : 'entreeFilaire.resume'" tag="p" class="mt-1 text-sm text-gray-500">
      <template #nom><strong class="text-gray-700">{{ device.wired_label }}</strong></template>
      <template #role>{{ roleLabel }}</template>
      <template #etat><strong>{{ etatLabel }}</strong></template>
      <template #note><span v-if="!etatFiable"
        class="text-gray-400"> — {{ etatNote }}</span></template>
    </i18n-t>

    <!-- Nom de ce qui est branché (05/10/2026) : « Détecteur de fumée », « Porte du garage »…
         C'est lui que disent l'historique, les SMS, les appels et les notifications. Il reste
         sur le serveur : le changer n'envoie rien à la carte, ne réveille pas une carte qui dort.
         Titulaire et admin seulement (le serveur refuse aux membres). -->
    <div v-if="peutRenommer" class="mt-4">
      <label class="block text-sm font-medium text-gray-700" :for="`nom-entree-${device.id}`">{{ $t('entreeFilaire.nomQ') }}</label>
      <div class="mt-1 flex gap-2">
        <input :id="`nom-entree-${device.id}`" v-model="nom" type="text" maxlength="40" :placeholder="$t('entreeFilaire.nomExemple')"
               class="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
               @keyup.enter="enregistrerNom">
        <button @click="enregistrerNom" :disabled="enregistrement || nom.trim() === (device.wired_label || '')"
                class="btn btn-primary whitespace-nowrap text-sm">{{ $t('entreeFilaire.nomEnregistrer') }}</button>
      </div>
      <p class="mt-1 text-xs" :class="avisNom && avisNom.ok === false ? 'text-red-600' : 'text-gray-500'">
        {{ avisNom ? avisNom.texte : $t('entreeFilaire.nomAide') }}
      </p>
    </div>
    <p v-else-if="device.wired_label" class="mt-2 text-sm text-gray-700">
      {{ $t('entreeFilaire.nomQ') }} : <strong>{{ device.wired_label }}</strong>
    </p>

    <div v-if="peut('alarm_24h_enable')" class="mt-4">
      <label class="block text-sm font-medium text-gray-700">{{ $t('entreeFilaire.surveilleeQ') }}</label>
      <choix-binaire class="mt-1" :titre="$t('entreeFilaire.surveilleeQ')" :busy="busy" :send="send"
                     :rapporte="rapporte.alarm_24h" :demande="device.alarm_24h"
                     :label-oui="$t('entreeFilaire.permanence')" :label-non="$t('entreeFilaire.seulementArme')"
                     cmd-oui="alarm_24h_enable" cmd-non="alarm_24h_disable" />
      <p class="mt-1 text-xs text-gray-500">
        {{ $t('entreeFilaire.surveilleeAide') }}
      </p>
    </div>

    <div v-if="peut('alarm_siren_enable')" class="mt-4">
      <label class="block text-sm font-medium text-gray-700">{{ $t('entreeFilaire.sireneQ') }}</label>
      <choix-binaire class="mt-1" :titre="$t('entreeFilaire.sireneQ')" :busy="busy" :send="send"
                     :rapporte="rapporte.alarm_siren" :demande="device.alarm_siren" oui-dabord
                     :label-oui="$t('entreeFilaire.oui')" :label-non="$t('entreeFilaire.nonSilencieuse')"
                     cmd-oui="alarm_siren_enable" cmd-non="alarm_siren_disable" />
      <p class="mt-1 text-xs text-gray-500">
        {{ $t('entreeFilaire.sireneAide') }}
      </p>
    </div>

    <template v-if="technique || peut('alarm_nc_enable') || peut('alarm_report_enable')">
      <div v-if="technique || peut('alarm_nc_enable')" class="mt-4">
        <label class="block text-sm font-medium text-gray-700">{{ $t('entreeFilaire.reposQ') }}</label>
        <choix-binaire class="mt-1" :titre="$t('entreeFilaire.reposQ')" :busy="busy" :send="send"
                       :rapporte="rapporte.alarm_nc" :demande="device.alarm_nc"
                       :label-oui="$t('entreeFilaire.nc')" :label-non="$t('entreeFilaire.no')"
                       cmd-oui="alarm_nc_enable" cmd-non="alarm_nc_disable" />
        <p class="mt-1 text-xs text-gray-500">{{ $t('entreeFilaire.switchAide') }}</p>
      </div>

      <div v-if="technique || peut('alarm_report_enable')" class="mt-4">
        <label class="block text-sm font-medium text-gray-700">{{ $t('entreeFilaire.remonteeQ') }}</label>
        <choix-binaire class="mt-1" :titre="$t('entreeFilaire.remonteeQ')" :busy="busy" :send="send"
                       :rapporte="rapporte.alarm_report" :demande="device.alarm_report" oui-dabord
                       :label-oui="$t('entreeFilaire.oui')" :label-non="$t('entreeFilaire.non')"
                       cmd-oui="alarm_report_enable" cmd-non="alarm_report_disable" />
        <!--
          🔴 Une ancienne légende disait « l'armement l'active pour déclencher l'alarme ». C'est
          FAUX, vérifié dans le firmware le 18/09 : la condition de tir est front montant + zone
          active + cooldown, `alarm_report` n'y figure pas. Un installateur qui coupe la remontée
          en croyant neutraliser l'entrée obtient l'inverse de ce qu'il voulait.
        -->
        <i18n-t keypath="entreeFilaire.remonteeAide" tag="p" class="mt-1 text-xs text-gray-500">
          <template #fort><strong class="text-gray-700">{{ $t('entreeFilaire.remonteeFort') }}</strong></template>
        </i18n-t>
      </div>
    </template>

    <!-- La combinaison qui prive de la moitié de l'information : le déclenchement ne tire que
         sur le front montant. Sans remontée d'état, on apprend qu'une alarme technique a
         COMMENCÉ, jamais qu'elle s'est terminée. Dit ici plutôt que découvert le jour où l'on
         attend en vain la fin d'une alerte. -->
    <div v-if="role.warn_no_report && peut('alarm_report_enable')"
         class="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
      <div class="font-semibold">{{ $t('entreeFilaire.finTitre') }}</div>
      <div class="mt-0.5">
        {{ $t('entreeFilaire.finTexte') }}
      </div>
      <button :disabled="busy" @click="send('alarm_report_enable')"
              class="mt-2 px-2 py-1 rounded border border-amber-300 bg-amber-100 hover:bg-amber-200 font-semibold">
        {{ $t('entreeFilaire.finBouton') }}
      </button>
    </div>
    <p class="mt-4 text-xs text-gray-500">{{ $t('entreeFilaire.aideEtat') }}</p>
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import apiService from '@/services/api.service';
import { texteErreur } from '@/i18n/serveur';
import ChoixBinaire from './ChoixBinaire.vue';

export default defineComponent({
  name: 'WiredInputCard',
  components: { ChoixBinaire },
  props: {
    device:    { type: Object, required: true },
    send:      { type: Function, required: true },
    busy:      { type: Boolean, default: false },
    technique: { type: Boolean, default: false },
    // Commandes autorisées : la vue client passe le filtre du serveur (`allowed_commands`),
    // l'admin n'en passe pas — tout lui est permis.
    peut:      { type: Function, default: () => true }
  },
  setup(props) {
    const { t } = useI18n();
    // Rôle calculé par le SERVEUR (`wired_role`) et seulement affiché ici. Le repli ne sert
    // qu'à une fiche chargée avant la migration des colonnes.
    const role = computed(() => props.device.wired_role
      || { h24: false, siren: true, nature: 'intrusion', report: false, warn_no_report: false });

    // Libellé tel qu'il s'insère dans la phrase (déjà en minuscule en français, comme le
    // faisait `toLowerCase()` : l'allemand garde ses majuscules).
    const roleLabel = computed(() => {
      switch (role.value.nature) {
        case 'technique_sonore':      return t('entreeFilaire.roleSonore');
        case 'technique_silencieuse': return t('entreeFilaire.roleSilencieuse');
        default:                      return t('entreeFilaire.roleIntrusion');
      }
    });

    // 🔴 Le niveau de l'entrée n'a qu'une source : l'événement `alarm_input`, conditionné par
    // la remontée d'état. Coupée, la valeur se fige ; après un redémarrage, on ne sait plus rien.
    // Annoncer « au repos » dans ces cas-là serait un faux rassurant : on dit « inconnu ».
    const etatFiable = computed(() => {
      const d = props.device;
      if (d.alarm_input_state === null || d.alarm_input_state === undefined) return false;
      return role.value.report || d.state === 'armed';
    });
    const etatLabel = computed(() => !etatFiable.value ? t('entreeFilaire.inconnu')
      : (props.device.alarm_input_state ? t('entreeFilaire.activee') : t('entreeFilaire.repos')));
    const etatNote = computed(() => {
      const d = props.device;
      if (d.alarm_input_state === null || d.alarm_input_state === undefined) {
        return t('entreeFilaire.noteDemarrage');
      }
      return t('entreeFilaire.noteCoupee');
    });

    // Ce que la carte dit appliquer : c'est lui qui coche les options (règle de ChoixBinaire).
    const rapporte = computed(() => props.device.reported_config || {});

    // Nom de l'entrée : l'admin et le titulaire le changent ; un membre le voit seulement.
    const store = useStore();
    const peutRenommer = computed(() => {
      const u = store.getters['auth/user'] || {};
      return u.role === 'admin' || u.memberRole === 'titulaire';
    });
    const nom = ref(props.device.wired_label || '');
    watch(() => props.device.wired_label, v => { nom.value = v || ''; });
    const enregistrement = ref(false), avisNom = ref(null);
    const enregistrerNom = async () => {
      enregistrement.value = true; avisNom.value = null;
      try {
        await apiService.updateDevice(props.device.id, { wired_label: nom.value.trim() });
        // eslint-disable-next-line vue/no-mutating-props
        props.device.wired_label = nom.value.trim() || null;
        avisNom.value = { ok: true, texte: t('entreeFilaire.nomEnregistre') };
      } catch (e) {
        avisNom.value = { ok: false, texte: texteErreur(e, t('entreeFilaire.nomImpossible')) };
      } finally {
        enregistrement.value = false;
      }
    };

    return { role, roleLabel, etatFiable, etatLabel, etatNote, rapporte,
             peutRenommer, nom, enregistrement, avisNom, enregistrerNom };
  }
});
</script>
